import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Stack,
  alpha
} from "@mui/material";
import { Close, PublicRounded, SecurityRounded, LinkRounded, BadgeRounded } from "@mui/icons-material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

// Identity Providers Update Component
function IdentityProvidersUpdate({
  handleModalClose,
  isNew,
  identityProviderData,
  refetch
}) {
  // HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: "onChange",
    defaultValues: isNew ? {
      name: "",
      logo_uri: "",
      client_id: "",
      client_secret: "",
      redirect_uri: "",
      authorization_uri: "",
      token_endpoint_uri: "",
      user_info_endpoint_uri: ""
    } : identityProviderData
  });

  // CUSTOM HOOK
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // MUTATION
  const { mutate: create, isLoading: isCreating } = useMutation((formData) => axiosPrivate.post(`/identityProviders`, formData));
  const { mutate: update, isLoading: isUpdating } = useMutation((formData) =>
    axiosPrivate.put(`/identityProviders/${identityProviderData.id}`, formData)
  );

  const onSubmit = (formData) => {
    if (isNew) {
      create(formData, {
        onSuccess: () => {
          enqueueSnackbar(`Provider created successfully.`, { variant: "success" });
          handleModalClose();
          refetch();
        }
      });
    } else {
      update(formData, {
        onSuccess: () => {
          enqueueSnackbar(`Provider updated successfully.`, { variant: "success" });
          handleModalClose();
          refetch();
        }
      });
    }
  };

  const actionLoading = isCreating || isUpdating;

  return (
    <Grid container sx={{ minHeight: "100vh", justifyContent: "center", alignItems: { xs: "flex-end", sm: "center" }, p: { xs: 0, sm: 2 } }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: { xs: '100%', sm: '500px' },
          borderRadius: { xs: '24px 24px 0 0', sm: 6 },
          boxShadow: theme => theme.palette.mode === 'light'
            ? "0px 20px 60px rgba(0, 0, 0, 0.15)"
            : `0px 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '95vh'
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'secondary.main', borderRadius: 2 }}>
              <PublicRounded />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{isNew ? "Add Provider" : "Update Provider"}</Typography>
          </Stack>
          <IconButton onClick={handleModalClose} size="small"><Close /></IconButton>
        </Box>

        <Box sx={{ p: 4, overflowLink: 'auto' }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3}>
              <FormSection title="Provider Identity" icon={<BadgeRounded fontSize="inherit" />}>
                <Stack spacing={2} sx={{ mt: 1.5 }}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Provider Name" error={!!errors.name} helperText={errors.name?.message} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                  <Controller
                    name="logo_uri"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Logo URL (Optional)" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                </Stack>
              </FormSection>

              <FormSection title="Security Credentials" icon={<SecurityRounded fontSize="inherit" />}>
                <Stack spacing={2} sx={{ mt: 1.5 }}>
                  <Controller
                    name="client_id"
                    control={control}
                    rules={{ required: "Client ID is required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Client ID" error={!!errors.client_id} helperText={errors.client_id?.message} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                  <Controller
                    name="client_secret"
                    control={control}
                    rules={{ required: isNew ? "Client Secret is required" : false }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" type="password" label={isNew ? "Client Secret" : "Client Secret (Optional)"} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                </Stack>
              </FormSection>

              <FormSection title="Endpoints" icon={<LinkRounded fontSize="inherit" />}>
                <Stack spacing={2} sx={{ mt: 1.5 }}>
                  <Controller
                    name="authorization_uri"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Authorization URI" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                  <Controller
                    name="token_endpoint_uri"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Token Endpoint" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                  <Controller
                    name="user_info_endpoint_uri"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="User Info Endpoint" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                  <Controller
                    name="redirect_uri"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <TextField {...field} fullWidth size="small" label="Our Redirect URI" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }} />
                    )}
                  />
                </Stack>
              </FormSection>

              <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                <Button fullWidth variant="outlined" color="inherit" onClick={handleModalClose} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none' }}>Cancel</Button>
                <Button fullWidth variant="contained" type="submit" disabled={actionLoading} sx={{ borderRadius: 3, py: 1.2, fontWeight: 700, textTransform: 'none', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' }, boxShadow: theme => `0 8px 20px ${alpha(theme.palette.common.black, 0.2)}` }}>
                  {actionLoading ? <CircularProgress size={24} color="inherit" /> : (isNew ? "Register" : "Update")}
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </Paper>
    </Grid>
  );
}

function FormSection({ title, icon, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', fontSize: 18, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography>
      </Stack>
      {children}
    </Box>
  );
}

export default IdentityProvidersUpdate;